import { useState } from "react";
import { Field, SaveBar, useDraft } from "@/components/hq/useDraft";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Upload } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { videoSrc } from "@/components/academy/TrainingTab";

const inputCls = "rounded-sm bg-[#0A0A0A]";
const monoCls = "mono rounded-sm bg-[#0A0A0A] text-sm";

const VideoField = ({ stepId, value, onChange }) => {
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/videos", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      onChange(data.url);
      toast.success(`Uploaded ${data.filename} — remember to save`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label="TUTORIAL VIDEO">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          data-testid={`hq-step-video-${stepId}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste a URL or upload a file"
          className={monoCls}
        />
        <label
          data-testid={`hq-step-upload-${stepId}`}
          className="mono flex cursor-pointer items-center gap-1 whitespace-nowrap border border-border px-3 py-2 text-[11px] tracking-widest text-primary hover:border-primary"
        >
          <Upload size={12} />
          {busy ? "UPLOADING…" : "UPLOAD"}
          <input
            type="file"
            accept="video/mp4,video/quicktime,video/x-m4v,video/webm"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
      </div>
      {value ? (
        <video
          src={videoSrc(value)}
          controls
          preload="metadata"
          className="mt-2 max-h-64 w-auto max-w-full rounded-sm border border-border bg-black"
        />
      ) : null}
      <p className="mono mt-1 text-[10px] text-muted-foreground">
        MP4 (H.264) plays everywhere — browsers cannot decode most .mov files.
      </p>
    </Field>
  );
};

export const CurriculumBuilder = () => {
  const { draft, update, save, dirty } = useDraft("modules");
  if (!draft) return null;

  const setModule = (id, patch) =>
    update(draft.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const setStep = (mid, sid, patch) =>
    update(
      draft.map((m) =>
        m.id === mid
          ? { ...m, steps: m.steps.map((s) => (s.id === sid ? { ...s, ...patch } : s)) }
          : m
      )
    );

  return (
    <div data-testid="hq-curriculum">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="curriculum"
        addLabel="+ Module"
        onAdd={() =>
          update([
            ...draft,
            { id: `m-${Date.now()}`, title: "New module", summary: "", steps: [] },
          ])
        }
      />
      <div className="space-y-6">
        {draft.map((m) => (
          <div key={m.id} className="border border-border bg-[#141414] rounded-sm p-5" data-testid={`hq-module-${m.id}`}>
            <div className="flex items-center gap-3">
              <Input
                data-testid={`hq-module-title-${m.id}`}
                value={m.title}
                onChange={(e) => setModule(m.id, { title: e.target.value })}
                className={`${inputCls} display text-xl uppercase`}
              />
              <span className="mono text-[10px] text-muted-foreground">{m.id}</span>
              <button
                data-testid={`hq-module-delete-${m.id}`}
                onClick={() => update(draft.filter((x) => x.id !== m.id))}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <Input
              value={m.summary}
              onChange={(e) => setModule(m.id, { summary: e.target.value })}
              placeholder="Module summary (operator-visible)"
              className={`${inputCls} mt-3`}
            />

            <div className="mt-5 space-y-4">
              {(m.steps || []).map((s) => (
                <div key={s.id} className="border border-border/70 bg-[#101010] rounded-sm p-4" data-testid={`hq-step-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <Input
                      data-testid={`hq-step-title-${s.id}`}
                      value={s.title}
                      onChange={(e) => setStep(m.id, s.id, { title: e.target.value })}
                      className={inputCls}
                    />
                    <select
                      data-testid={`hq-step-tag-${s.id}`}
                      value={s.tag || ""}
                      onChange={(e) => setStep(m.id, s.id, { tag: e.target.value })}
                      className="mono h-9 rounded-sm border border-border bg-[#0A0A0A] px-2 text-xs"
                    >
                      <option value="">UNTAGGED</option>
                      <option value="GOOD">GOOD</option>
                      <option value="RESHOOT">RESHOOT</option>
                    </select>
                    <button
                      onClick={() =>
                        setModule(m.id, { steps: m.steps.filter((x) => x.id !== s.id) })
                      }
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <Field label="OPERATOR SUMMARY">
                      <Textarea
                        value={s.summary}
                        onChange={(e) => setStep(m.id, s.id, { summary: e.target.value })}
                        className={monoCls}
                        rows={2}
                      />
                    </Field>
                    <VideoField
                      stepId={s.id}
                      value={s.videoUrl}
                      onChange={(url) => setStep(m.id, s.id, { videoUrl: url })}
                    />
                    <Field label="COACH NOTES (HQ ONLY)">
                      <Textarea
                        data-testid={`hq-step-coach-${s.id}`}
                        value={s.coachNotes || ""}
                        onChange={(e) => setStep(m.id, s.id, { coachNotes: e.target.value })}
                        className={monoCls}
                        rows={2}
                      />
                    </Field>
                    <Field label="FREEZE-FRAME NOTES (HQ ONLY)">
                      <Textarea
                        value={s.freezeFrameNotes || ""}
                        onChange={(e) => setStep(m.id, s.id, { freezeFrameNotes: e.target.value })}
                        className={monoCls}
                        rows={2}
                      />
                    </Field>
                    <Field label="SHOT DURATION (HQ ONLY)">
                      <Input
                        value={s.shotDuration || ""}
                        onChange={(e) => setStep(m.id, s.id, { shotDuration: e.target.value })}
                        className={monoCls}
                        placeholder="1:30"
                      />
                    </Field>
                  </div>
                </div>
              ))}
              <button
                data-testid={`hq-step-add-${m.id}`}
                onClick={() =>
                  setModule(m.id, {
                    steps: [
                      ...(m.steps || []),
                      {
                        id: `s-${Date.now()}`,
                        title: "New step",
                        summary: "",
                        videoUrl: "",
                        coachNotes: "",
                        freezeFrameNotes: "",
                        shotDuration: "",
                        tag: "",
                      },
                    ],
                  })
                }
                className="mono text-[11px] tracking-widest text-primary hover:underline"
              >
                + ADD STEP
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ClipQueue = () => {
  const { draft, update, save, dirty } = useDraft("clips");
  if (!draft) return null;
  const setClip = (id, patch) => update(draft.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  return (
    <div data-testid="hq-clip-queue">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="clips"
        addLabel="+ Clip"
        onAdd={() =>
          update([
            ...draft,
            {
              id: `clip-${Date.now()}`,
              title: "New clip",
              operator: "",
              moduleId: "",
              tag: "",
              status: "pending",
              coachNotes: "",
              freezeFrameNotes: "",
              shotDuration: "",
              videoUrl: "",
            },
          ])
        }
      />
      <div className="space-y-3">
        {draft.map((c) => (
          <div key={c.id} className="border border-border bg-[#141414] rounded-sm p-4" data-testid={`hq-clip-${c.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Input
                data-testid={`hq-clip-title-${c.id}`}
                value={c.title}
                onChange={(e) => setClip(c.id, { title: e.target.value })}
                className={`${inputCls} md:max-w-sm`}
              />
              <select
                data-testid={`hq-clip-tag-${c.id}`}
                value={c.tag || ""}
                onChange={(e) => setClip(c.id, { tag: e.target.value })}
                className={`mono h-9 rounded-sm border px-2 text-xs bg-[#0A0A0A] ${
                  c.tag === "GOOD"
                    ? "border-[#34C759] text-[#34C759]"
                    : c.tag === "RESHOOT"
                    ? "border-destructive text-destructive"
                    : "border-border"
                }`}
              >
                <option value="">UNTAGGED</option>
                <option value="GOOD">GOOD</option>
                <option value="RESHOOT">RESHOOT</option>
              </select>
              <select
                data-testid={`hq-clip-status-${c.id}`}
                value={c.status || "pending"}
                onChange={(e) => setClip(c.id, { status: e.target.value })}
                className="mono h-9 rounded-sm border border-border bg-[#0A0A0A] px-2 text-xs"
              >
                <option value="pending">PENDING</option>
                <option value="approved">APPROVED</option>
                <option value="rejected">REJECTED</option>
              </select>
              <Input
                value={c.shotDuration || ""}
                onChange={(e) => setClip(c.id, { shotDuration: e.target.value })}
                placeholder="0:30"
                className={`${monoCls} w-24`}
              />
              <button
                onClick={() => update(draft.filter((x) => x.id !== c.id))}
                className="ml-auto text-muted-foreground hover:text-destructive"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <Field label="COACH NOTES">
                <Textarea
                  data-testid={`hq-clip-coach-${c.id}`}
                  value={c.coachNotes || ""}
                  onChange={(e) => setClip(c.id, { coachNotes: e.target.value })}
                  className={monoCls}
                  rows={2}
                />
              </Field>
              <Field label="FREEZE-FRAME NOTES">
                <Textarea
                  value={c.freezeFrameNotes || ""}
                  onChange={(e) => setClip(c.id, { freezeFrameNotes: e.target.value })}
                  className={monoCls}
                  rows={2}
                />
              </Field>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
